import type { INPUT_METHOD } from '@atlaskit/editor-common/analytics/types/enums';
import { insertSelectedItem } from '@atlaskit/editor-common/insert';
import type { QuickInsertItem } from '@atlaskit/editor-common/provider-factory/quick-insert-provider';
import type { Command } from '@atlaskit/editor-common/types/command';
import type { EditorCommand } from '@atlaskit/editor-common/types/editor-command';
import type { Fragment, Node } from '@atlaskit/editor-prosemirror/model';

import type { OpenElementBrowserOptions } from '../../quickInsertPluginType';
import { pluginKey } from '../plugin-key';

export const openElementBrowser =
	({ category }: OpenElementBrowserOptions = {}): EditorCommand =>
	({ tr }) =>
		tr.setMeta(pluginKey, {
			elementBrowserInitialCategory: category,
			isElementBrowserOpen: true,
		});

export const closeElementBrowser = (): Command => (state, dispatch) => {
	if (dispatch) {
		dispatch(
			state.tr.setMeta(pluginKey, {
				elementBrowserInitialCategory: undefined,
				isElementBrowserOpen: false,
			}),
		);
	}
	return true;
};

/**
 * @private
 * @deprecated {@link https://hello.atlassian.net/browse/ENGHEALTH-62138 Internal documentation for deprecation (no external access)} Tracked by EDITOR-8422. Use `openElementBrowser` instead.
 */
export const openElementBrowserModal: EditorCommand = ({ tr }) =>
	tr.setMeta(pluginKey, { isElementBrowserModalOpen: true });

/**
 * @private
 * @deprecated {@link https://hello.atlassian.net/browse/ENGHEALTH-62138 Internal documentation for deprecation (no external access)} Tracked by EDITOR-8422. Use `closeElementBrowser` instead.
 */
export const closeElementBrowserModal = (): Command => (state, dispatch) => {
	if (dispatch) {
		dispatch(state.tr.setMeta(pluginKey, { isElementBrowserModalOpen: false }));
	}
	return true;
};

export const createInsertItem = (
	onInsert?: (item: QuickInsertItem) => void,
): ((
	item: QuickInsertItem,
	source?: INPUT_METHOD.QUICK_INSERT | INPUT_METHOD.TOOLBAR | INPUT_METHOD.ELEMENT_BROWSER,
) => Command) => insertItem(onInsert);

// this method was adapted from the typeahead plugin so we respect the API for quick insert items
const insertItem =
	(onInsert?: (item: QuickInsertItem) => void) =>
	(
		item: QuickInsertItem,
		source?: INPUT_METHOD.QUICK_INSERT | INPUT_METHOD.TOOLBAR | INPUT_METHOD.ELEMENT_BROWSER,
	): Command =>
	(state, dispatch) => {
		const insert = (
			maybeNode?: Node | Object | string | Fragment,
			opts: { selectInlineNode?: boolean } = {},
		) => {
			return insertSelectedItem(maybeNode, opts)(state, state.tr, state.selection.head);
		};

		const tr = item.action(insert, state, source);

		/** @note There is no transaction when called without a search currently (different insert) */
		if (tr && dispatch) {
			dispatch(tr);
		}

		onInsert?.(item);

		return true;
	};
